<template>
    <div>
        <div v-if="fileType === 'image' && croppedImage" class="preview-container">
            <img :src="croppedImagePreview" :style="{ height: croppedImageHeight + 'px' }" alt="Cropped Image Preview" class="cropped-image-preview mb-3">

            <div class="form-group d-flex align-items-center justify-content-start">
                <label :for="`fileUpload-${id}`" class="btn btn-primary btn-xs mb-0">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="currentColor"
                    >
                        <path d="M12 2a7 7 0 017 7h2a5 5 0 010 10H5a5 5 0 010-10h2a7 7 0 017-7zm-1 11v5a1 1 0 102 0v-5h2a1 1 0 10-.293-.707L13 10.414V2.5a1 1 0 10-2 0v7.914l-1.707 1.707A1 1 0 009 14h2z" />
                    </svg>
                    Choose Another File
                </label>
                <input
                    type="file"
                    :id="`fileUpload-${id}`"
                    @change="onFileChange"
                    class="form-control-file d-none"
                    ref="fileInput"
                >
            </div>
        </div>

        <div v-else-if="fileType === 'pdf' && fileName" class="pdf-area">
            <p class="text-2" style="margin-bottom: -1px; margin-top: 20px;">{{ fileName }}</p>
            <div class="form-group d-flex align-items-center">
                <label :for="`fileUpload-${id}`" class="btn btn-primary btn-xs mb-0">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="currentColor"
                    >
                        <path d="M12 2a7 7 0 017 7h2a5 5 0 010 10H5a5 5 0 010-10h2a7 7 0 017-7zm-1 11v5a1 1 0 102 0v-5h2a1 1 0 10-.293-.707L13 10.414V2.5a1 1 0 10-2 0v7.914l-1.707 1.707A1 1 0 009 14h2z" />
                    </svg>
                    Choose Another File
                </label>
                <input
                    type="file"
                    :id="`fileUpload-${id}`"
                    @change="onFileChange"
                    class="form-control-file d-none"
                    ref="fileInput"
                >
                <button
                    v-if="!hideSave"
                    class="btn btn-xs ms-2"
                    @click="uploadFile"
                    :disabled="!fileName || errorMessage"
                    :class="{
                    'btn-success': fileStatus === 'File Saved',
                    'btn-info': fileStatus === 'Save File',
                    'btn-loading': loading
                }"
                >
                <span v-if="fileStatus === 'Save File'">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="currentColor"
                    >
                        <path d="M19 3h-4.18A3 3 0 0012 1H9a3 3 0 00-3 3v2H5a2 2 0 00-2 2v12a2 2 0 002 2h14a2 2 0 002-2V7a4 4 0 00-2-4zM8 4a1 1 0 011-1h3a1 1 0 011 1v5H8V4zm8 16H8v-5h8v5zm3-8H5V7h2v3h10V7h2v5z" />
                    </svg>
                </span>
                    <span v-if="fileStatus === 'File Saved'">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="currentColor"
                        class="me-1"
                    >
                        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" />
                        <path d="M8 12l2.5 2.5L16 9" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </span>
                    {{ fileStatus }}
                </button>
            </div>
        </div>

        <div v-else>
            <div
                class="file-drop-area"
                @click="triggerFileInput"
                @dragover.prevent="onDragOver"
                @dragleave="onDragLeave"
                @drop.prevent="onDrop"
                :class="{ dragging: isDragging }"
            >
                <span class="choose-file-button">Choose file</span>
                <span class="file-message">or drag and drop file here</span>
                <span v-if="fileName" class="ml-2">{{ fileName }}</span>
            </div>
            <input
                type="file"
                ref="fileInput"
                @change="onFileChange"
                :accept="fileType === 'pdf' ? 'image/*,application/pdf' : fileType === 'image' ? 'image/*' : fileType === 'csv' ? '.csv' : '.xls,.xlsx'"
                class="form-control-file d-none"
            >
        </div>

        <div v-if="errorMessage" class="text-danger mt-2">
            {{ errorMessage }}
        </div>

        <div v-if="fileType === 'image' && showCropper" class="mt-3">
            <h6>Crop Your Image</h6>
            <div class="crop-container">
                <img ref="cropImage" :src="imagePreview" alt="Image Preview" class="img-fluid">
            </div>
            <div class="mt-3 d-flex justify-content-between">
                <button class="btn btn-primary btn-xs" @click="confirmCrop">Confirm Crop</button>
                <button class="btn btn-secondary btn-xs" @click="resetCropper">Cancel</button>
            </div>
        </div>
    </div>
</template>

<script>
import Cropper from "cropperjs";
import "cropperjs/dist/cropper.css";
import Form from "../../../core/forms/form";

export default {
    name: "FileUpload",
    props: {
        fileType: {
            type: String,
            default: "image",
            validator: (value) => ["image", "pdf", "csv", "excel"].includes(value),
        },
        value: {
            type: [Object, String, File],
            default: null,
        },
        url: {
            type: String,
            default: "",
        },
        documentType: {
            type: String,
            default: "",
        },
        aspectRatio: {
            type: Number,
            default: 16 / 9,
        },
        croppedImageHeight: {
            type: Number,
            default: 150,
        },
        hideSave: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            id: Math.random().toString(36).substr(2, 9),
            fileName: null,
            imagePreview: null,
            croppedImage: null,
            croppedImagePreview: null,
            cropper: null,
            fileStatus: "Save File",
            showCropper: false,
            isDragging: false,
            errorMessage: null,
            maxFileSize: 10 * 1024 * 1024,
        };
    },
    watch: {
        value: {
            immediate: true,
            handler(newValue) {
                if (newValue && this.fileType === "image") {
                    this.croppedImagePreview = URL.createObjectURL(newValue);
                }
            },
        },
    },
    methods: {
        triggerFileInput() {
            this.$refs.fileInput.click();
        },

        validateFile(file) {
            const validImageTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
            const validPdfTypes = ["application/pdf"];
            const validCsvTypes = ["text/csv", "application/vnd.ms-excel"];
            const validExcelTypes = [
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                "application/vnd.ms-excel",
                "application/octet-stream",
                "application/wps-office.xlsx"
            ];

            if (file.size > this.maxFileSize) {
                return `File size exceeds the maximum limit of 5MB.`;
            }

            if (this.fileType === "image" && !validImageTypes.includes(file.type)) {
                return "Invalid file type. Please upload a valid image (JPEG, JPG, PNG, GIF).";
            }

            if (this.fileType === "pdf" && !validPdfTypes.includes(file.type) && !validImageTypes.includes(file.type)) {
                return "Invalid file type. Please upload a valid PDF or image file (JPEG, JPG, PNG, GIF).";
            }

            if (this.fileType === "csv" && !validCsvTypes.includes(file.type)) {
                return "Invalid file type. Please upload a valid CSV file.";
            }

            if (this.fileType === "excel" && !validExcelTypes.includes(file.type)) {
                return "Invalid file type. Please upload a valid Excel file (.xls or .xlsx).";
            }

            return null;
        },

        onFileChange(event) {
            const file = event.target.files[0];
            if (file) {
                const validationError = this.validateFile(file);
                if (validationError) {
                    this.errorMessage = validationError;
                    this.resetCropper();
                    return;
                }

                this.errorMessage = null;
                this.fileName = file.name;
                this.fileStatus = 'Save File';

                if (this.fileType === "image") {
                    this.resetCropper();
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        this.imagePreview = e.target.result;
                        this.showCropper = true;
                        this.$nextTick(() => {
                            this.initializeCropper();
                        });
                    };
                    reader.readAsDataURL(file);
                } else {
                    this.$emit("input", file);
                }
            }
        },
        initializeCropper() {
            const cropImage = this.$refs.cropImage;
            this.cropper = new Cropper(cropImage, {
                aspectRatio: this.aspectRatio,
                viewMode: 1,
                background: false,
                zoomable: true,
                dragMode: "crop",
                scalable: true,
            });
        },
        resetCropper() {
            if (this.cropper) {
                this.cropper.destroy();
                this.cropper = null;
            }
            this.imagePreview = null;
            this.croppedImage = null;
            this.fileName = null;
            this.showCropper = false;
        },
        confirmCrop() {
            if (this.cropper) {
                const canvas = this.cropper.getCroppedCanvas();
                canvas.toBlob((blob) => {
                    this.croppedImage = blob;
                    this.croppedImagePreview = URL.createObjectURL(blob);
                    this.$emit("input", blob);
                });
                this.resetCropper();
            }
        },
        async uploadFile() {
            if (!this.value) {
                this.errorMessage = "No file selected for upload.";
                return;
            }

            const uploadForm = new Form({
                document: this.value,
                type: this.documentType,
            });

            this.fileStatus = 'Uploading File ...';
            this.loading = true;
            uploadForm.upload(this.url)
                .then((response) => {
                    this.$emit("upload-success", response.data);
                    this.errorMessage = null;
                    this.loading = false;
                    this.fileStatus = 'File Saved';
                })
                .catch((error) => {
                    this.errorMessage =
                        error.response?.data?.message || "Error uploading file.";
                });
        },
        onDragOver() {
            this.isDragging = true;
        },
        onDragLeave() {
            this.isDragging = false;
        },
        onDrop(event) {
            this.isDragging = false;
            const file = event.dataTransfer.files[0];
            if (file) {
                const validationError = this.validateFile(file);
                if (validationError) {
                    this.errorMessage = validationError;
                    this.resetCropper();
                    return;
                }

                this.errorMessage = null;
                this.fileName = file.name;
                this.onFileChange({ target: { files: [file] } });
            }
        },
    },
};

</script>


<style scoped>
    .form-group {
        gap: 10px;
    }

    .crop-container {
        width: 100%;
        max-height: 300px;
        overflow: hidden;
    }
    .cropped-image-preview {
        height: 150px;
        object-fit: contain;
        border: 1px solid #ccc;
        display: block;
    }
    .file-drop-area {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 80px;
        border: 2px dashed #ccc;
        border-radius: 5px;
        background-color: #f8f9fa;
        text-align: center;
        cursor: pointer;
        transition: border-color 0.3s ease;
    }
    .pdf-area {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 80px;
        border-radius: 5px;
        background-color: #f8f9fa;
        text-align: center;
        cursor: pointer;
        transition: border-color 0.3s ease;
    }
    .file-drop-area.dragging {
        border-color: #28a745;
        background-color: #e9ecef;
    }
    .choose-file-button {
        font-weight: bold;
        color: #28a745;
    }
    .file-message {
        font-size: 14px;
        color: #6c757d;
    }
    .text-danger {
        color: #dc3545;
        font-size: 14px;
    }
    .btn-xs {
        padding: 0.25rem 0.5rem;
        font-size: small;
    }
</style>
